// rose-gold — Pre-wedding fotixa preview card.
//
// A tender, ceremonial preview built for f01 Fotixa (engagement). The frame
// is a layered rose-gold gradient (rose → champagne → rose) that evokes
// metallic foil. A round photo medallion sits in the centre, matted by a
// rose-gold ring, and is flanked by:
//   • "FOTIXA TO'YI" eyebrow at top in roman small-caps
//   • Names in Pinyon Script — large, delicate calligraphy
//   • "OILALAR UCHRASHUVI · 5 APREL 2026" caption at the bottom
//
// All values resolve from the LayoutCtx so the same component renders the
// preview tile, the editor preview, and the printed card without forks.
import { Photo, type LayoutCtx } from "./_shared";

// Rose-gold metallic gradient — alternating warm rose and champagne stops
// give the borderline foil a believable highlight.
const FOIL_GRADIENT =
  "linear-gradient(135deg, #D4A5A5 0%, #E9CCB6 22%, #F4E2CE 45%, #E9CCB6 65%, #D4A5A5 100%)";

export function RoseGoldLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  // Format the date as "5 APREL 2026" for the bottom caption when the
  // template's date is in DD.MM.YYYY form. Falls back to the raw string.
  const captionDate = formatFotixaDate(date);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL_GRADIENT,
        padding: "5%",
        display: "flex",
      }}
    >
      {/* Inner ivory card sits inside the gradient foil border. */}
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          // Subtle inner hairline to read as a printed card edge.
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow — small caps, rose-gold */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          FOTIXA TO'YI
        </div>

        {/* Circular photo medallion with rose-gold matte ring. */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Outer foil ring */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL_GRADIENT,
              padding: "5%",
            }}
          >
            {/* Inner matte */}
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${accent}33, ${bg})`,
                      }}
                    />
                  }
                />
                {/* Soft warm wash to keep the photo tender. */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 55%, ${accent}22 100%)`,
                  }}
                />
              </div>
            </div>
          </div>
          {/* Tiny rose-gold dots at compass points — subtle ornaments. */}
          {[0, 90, 180, 270].map((deg) => (
            <span
              key={deg}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: accent,
                transform: `rotate(${deg}deg) translate(0, calc(-50% - 50% + 1px))`,
                transformOrigin: "0 0",
                opacity: 0.85,
              }}
              aria-hidden
            />
          ))}
        </div>

        {/* Names — Pinyon Script, large, delicate */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "normal",
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          OILALAR UCHRASHUVI · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

// "05.04.2026" → "5 APREL 2026". Accepts any DD.MM.YYYY string.
function formatFotixaDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  const monthName = months[month - 1] ?? "";
  return `${day} ${monthName} ${year}`;
}
